import React, { useEffect, useState } from "react";
import { Form, Button, Container, Row, Col } from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import { storage } from "../../firebaseConfig";
import {
  getDownloadURL,
  ref,
  uploadBytes,
  deleteObject,
} from "firebase/storage";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

const ProjectSubmit = () => {
  const categories = ["graphic design", "motion graphics", "3D"];
  const softwares = [
    "Adobe Illustrator",
    "Adobe Photoshop",
    "Adobe After Effects",
    "Adobe Premiere Pro",
    "Cinema 4D",
    "Keyshot",
    "Figma",
  ];

  const [project, setProject] = useState({
    name: "",
    description: "",
    url: "",
    videoUrl: "",
    category: [],
    software: [],
    gallery: [],
  });

  const [galleryState, setGalleryState] = useState({ existing: [], new: [] });
  const [editingProject, setEditingProject] = useState(null);
  const [projects, setProjects] = useState([]);

  // ---- Form change handler ----
  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    if (type === "checkbox") {
      setProject((prev) => ({
        ...prev,
        [name]: checked
          ? [...prev[name], value]
          : prev[name].filter((v) => v !== value),
      }));
    } else if (type === "file") {
      const newFiles = Array.from(files).map((file) => ({
        file,
        preview: URL.createObjectURL(file),
      }));
      setGalleryState((prev) => ({ ...prev, new: [...prev.new, ...newFiles] }));
    } else {
      setProject((prev) => ({ ...prev, [name]: value }));
    }
  };

  // ---- Cleanup object URLs ----
  useEffect(() => {
    return () => {
      galleryState.new.forEach((item) => URL.revokeObjectURL(item.preview));
    };
  }, [galleryState.new]);

  // ---- Image delete ----
  const handleImageDelete = async (url, type) => {
    try {
      if (type === "existing") {
        const fileRef = ref(storage, url);
        await deleteObject(fileRef);
        setGalleryState((prev) => ({
          ...prev,
          existing: prev.existing.filter((img) => img !== url),
        }));
      } else {
        setGalleryState((prev) => ({
          ...prev,
          new: prev.new.filter((img) => img.preview !== url),
        }));
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      toast.error("Failed to delete image. Please try again later.");
    }
  };

  // ---- Fetch projects ----
  const fetchProjects = async () => {
    try {
      const response = await axios.get(
        "https://mirza-portfolio-server.onrender.com/get-projects"
      );
      setProjects(response.data);
    } catch (error) {
      console.error("Failed to fetch projects", error);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  // ---- Submit project ----
  const handleSubmit = async (e) => {
    e.preventDefault();

    // --- Validation ---
    if (!project.name) return toast.error("Project name is required");
    if (project.category.length === 0)
      return toast.error("Select at least one category");
    if (project.software.length === 0)
      return toast.error("Select at least one software");

    try {
      // --- Upload new images in parallel ---
      const uploadedUrls = await Promise.all(
        galleryState.new.map(async (item) => {
          const fileRef = ref(storage, `gallery/${item.file.name}`);
          await uploadBytes(fileRef, item.file);
          return getDownloadURL(fileRef);
        })
      );

      const galleryUrls = [...galleryState.existing, ...uploadedUrls];

      const currentTime = new Date();
      const newIndex = editingProject
        ? editingProject.index
        : projects.length > 0
        ? Math.max(...projects.map((p) => p.index)) + 1
        : 0;

      const payload = {
        ...project,
        gallery: galleryUrls,
        dateAdded: editingProject ? editingProject.dateAdded : currentTime,
        index: newIndex,
      };

      let response;
      if (editingProject) {
        response = await axios.put(
          `https://mirza-portfolio-server.onrender.com/update-project/${editingProject.id}`,
          payload
        );
      } else {
        response = await axios.post(
          "https://mirza-portfolio-server.onrender.com/save-project",
          payload
        );
        setProjects((prevProjects) => [payload, ...prevProjects]);
      }

      if (response.status === 200) {
        toast.success(
          editingProject
            ? "Project updated successfully"
            : "Project submitted successfully"
        );
        fetchProjects();
        setProject({
          name: "",
          description: "",
          url: "",
          videoUrl: "",
          category: [],
          software: [],
          gallery: [],
        });
        setGalleryState({ existing: [], new: [] });
        setEditingProject(null);
      } else {
        toast.error("Failed to submit project. Please try again later.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to submit project. Please try again later.");
    }
  };

  // ---- Edit project ----
  const handleEdit = (proj) => {
    setProject({ ...proj });
    setGalleryState({ existing: proj.gallery || [], new: [] });
    setEditingProject(proj);
  };

  // ---- Delete project ----
  const handleDelete = async (id) => {
    try {
      await axios.delete(
        `https://mirza-portfolio-server.onrender.com/delete-project/${id}`
      );
      toast.success("Project deleted successfully");
      fetchProjects();
    } catch (error) {
      toast.error("Failed to delete project. Please try again later.");
    }
  };

  // ---- Drag & Drop reordering ----
  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const reordered = Array.from(projects);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);

    // index = pozicija u nizu
    const updatedProjects = reordered.map((p, i) => ({
      ...p,
      index: i,
    }));

    setProjects(updatedProjects);

    // Batch update
    try {
      await Promise.all(
        updatedProjects.map((p) =>
          axios.put(
            `https://mirza-portfolio-server.onrender.com/update-project/${p.id}`,
            { index: p.index }
          )
        )
      );
    } catch (error) {
      console.error("Failed to update project order", error);
    }
  };

  return (
    <Container style={{ marginTop: 100 }}>
      <h1 className="text-center">
        {editingProject ? "Edit Project" : "Submit Project"}
      </h1>
      <Form onSubmit={handleSubmit}>
        <Form.Group className="mt-3">
          <Form.Label>Name</Form.Label>
          <Form.Control
            type="text"
            name="name"
            value={project.name}
            onChange={handleChange}
          />
        </Form.Group>
        <Form.Group className="mt-3">
          <Form.Label>Description</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            name="description"
            value={project.description}
            onChange={handleChange}
          />
        </Form.Group>
        <Form.Group className="mt-3">
          <Form.Label>URL</Form.Label>
          <Form.Control
            type="text"
            name="url"
            value={project.url}
            onChange={handleChange}
          />
        </Form.Group>
        <Form.Group className="mt-3">
          <Form.Label>Video URL</Form.Label>
          <Form.Control
            type="text"
            name="videoUrl"
            value={project.videoUrl}
            onChange={handleChange}
          />
        </Form.Group>

        <Form.Group className="mt-3">
          <Form.Label>Categories</Form.Label>
          {categories.map((cat) => (
            <Form.Check
              key={cat}
              type="checkbox"
              label={cat}
              name="category"
              value={cat}
              checked={project.category.includes(cat)}
              onChange={handleChange}
            />
          ))}
        </Form.Group>

        <Form.Group className="mt-3">
          <Form.Label>Software Used</Form.Label>
          {softwares.map((soft) => (
            <Form.Check
              key={soft}
              type="checkbox"
              label={soft}
              name="software"
              value={soft}
              checked={project.software.includes(soft)}
              onChange={handleChange}
            />
          ))}
        </Form.Group>

        <Form.Group className="mt-3">
          <Form.Label>Gallery</Form.Label>
          <Form.Control
            type="file"
            multiple
            name="gallery"
            onChange={handleChange}
          />
          <div className="mt-3 d-flex flex-wrap">
            {galleryState.existing.map((url, i) => (
              <div
                key={i}
                style={{
                  position: "relative",
                  marginRight: 10,
                  marginBottom: 10,
                }}
              >
                <img
                  src={url}
                  alt={`Gallery ${i}`}
                  width={100}
                  height={100}
                  loading="lazy"
                />
                <Button
                  variant="danger"
                  size="sm"
                  style={{ position: "absolute", top: 0, right: 0 }}
                  onClick={() => handleImageDelete(url, "existing")}
                >
                  X
                </Button>
              </div>
            ))}
            {galleryState.new.map((item, i) => (
              <div
                key={i}
                style={{
                  position: "relative",
                  marginRight: 10,
                  marginBottom: 10,
                }}
              >
                <img
                  src={item.preview}
                  alt={`New Gallery ${i}`}
                  width={100}
                  height={100}
                  loading="lazy"
                />
                <Button
                  variant="danger"
                  size="sm"
                  style={{ position: "absolute", top: 0, right: 0 }}
                  onClick={() => handleImageDelete(item.preview, "new")}
                >
                  X
                </Button>
              </div>
            ))}
          </div>
        </Form.Group>

        <Button className="mt-4" variant="primary" type="submit">
          {editingProject ? "Update" : "Submit"}
        </Button>
      </Form>

      <h2 className="text-center mt-5">Projects</h2>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="projects">
          {(provided) => (
            <Row {...provided.droppableProps} ref={provided.innerRef}>
              {projects.map((proj, idx) => (
                <Draggable key={proj.id} draggableId={proj.id} index={idx}>
                  {(prov) => (
                    <Col
                      md={12}
                      className="mb-4"
                      ref={prov.innerRef}
                      {...prov.draggableProps}
                      {...prov.dragHandleProps}
                    >
                      <div className="project-card d-flex justify-content-between align-items-center">
                        <div>
                          <h3>{proj.name}</h3>
                          <p>
                            {Array.isArray(proj.category)
                              ? proj.category.join(", ")
                              : proj.category}
                          </p>
                        </div>
                        <div>
                          <Button
                            variant="secondary"
                            className="me-2"
                            onClick={() => handleEdit(proj)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="danger"
                            onClick={() => handleDelete(proj.id)}
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    </Col>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Row>
          )}
        </Droppable>
      </DragDropContext>
    </Container>
  );
};

export default ProjectSubmit;
